import { useEffect, useState } from 'react';
import type { ConversationMessageContent } from '../../types/conversation-message';
import type { ConversationMessageAttachmentImageSize } from '../../types/conversation-message';
import { CueStreamdownAudio } from '../markdown/overrides/audio';
import { CueStreamdownVideo } from '../markdown/overrides/video';
import {
  FileAttachmentCard,
  formatAttachmentSize,
  type AttachmentUnavailableInfo,
} from './file-attachment';
import { AttachmentImage } from './attachment-image';
import { sourceResolutionFailureCopy } from './attachment-preview-diagnostics';
import { resolveConversationAttachment } from './conversation-attachment';
import { useResolvedUrl } from './use-resolved-url';
import { hasNativeJSBridge } from '@/utils/bridge-runtime';

type AttachmentPreviewKind = 'audio' | 'file' | 'image' | 'video';

function fallbackUnavailable(
  kind: AttachmentPreviewKind
): AttachmentUnavailableInfo {
  if (kind === 'audio' || kind === 'image' || kind === 'video') {
    const copy = sourceResolutionFailureCopy(kind, 'missing-source');
    return {
      description: copy.description,
      label: copy.label,
    };
  }

  return {
    description:
      'The message did not include a preview URL or usable file reference.',
    label: 'File preview unavailable',
  };
}

export const ConversationAttachmentView = ({
  allowImageDownload = true,
  artifactId,
  className,
  compactImageActions = false,
  content,
  fallbackFileName = 'attachment',
  imageClassName,
  cachedImageSize,
  onImageLayout,
  onRequestFileAccess,
  previewKind,
}: {
  allowImageDownload?: boolean;
  artifactId?: string;
  className?: string;
  compactImageActions?: boolean;
  content: ConversationMessageContent;
  fallbackFileName?: string;
  imageClassName?: string;
  cachedImageSize?: ConversationMessageAttachmentImageSize;
  onImageLayout?: (imageSize: ConversationMessageAttachmentImageSize) => void;
  onRequestFileAccess?: (message: string) => void | Promise<void>;
  previewKind?: AttachmentPreviewKind;
}) => {
  const kind = (previewKind ?? content.type) as AttachmentPreviewKind;
  const attachment = resolveConversationAttachment(content, fallbackFileName);
  const [unavailable, setUnavailable] =
    useState<AttachmentUnavailableInfo | null>(null);
  const [showAsFile, setShowAsFile] = useState(false);
  const mediaResolvedUrl = useResolvedUrl(
    kind === 'audio' || kind === 'video'
      ? {
          src: attachment.url,
          filePath: attachment.displayParams.filePath,
          environmentId: attachment.displayParams.environmentId,
          agentId: attachment.displayParams.agentId,
          signature: attachment.displayParams.signature,
          fileRefs: attachment.fileRefs,
          preferSignedAgentFileShare: true,
        }
      : {}
  );

  useEffect(() => {
    setUnavailable(null);
    setShowAsFile(false);
  }, [
    attachment.agentId,
    attachment.displayParams.filePath,
    attachment.environmentId,
    attachment.fileName,
    attachment.mimeType,
    attachment.signature,
    attachment.url,
    kind,
  ]);

  const fileCard = ({
    reason = unavailable,
    showSourceMetadata = false,
  }: {
    reason?: AttachmentUnavailableInfo | null;
    showSourceMetadata?: boolean;
  } = {}) => (
    <FileAttachmentCard
      filename={attachment.fileName}
      contentType={attachment.mimeType ?? 'application/octet-stream'}
      path={attachment.sourcePath}
      url={attachment.url}
      environmentId={attachment.environmentId}
      sourceSessionId={attachment.nativePreviewSessionId}
      agentId={attachment.agentId}
      signature={attachment.signature}
      fileRefs={attachment.fileRefs}
      size={formatAttachmentSize(attachment.sizeBytes)}
      sizeBytes={attachment.sizeBytes}
      unavailable={reason}
      showSourceMetadata={showSourceMetadata}
      data-artifact={artifactId}
      onRequestFileAccess={onRequestFileAccess}
      className={className}
    />
  );

  const handlePreviewUnavailable = () => {
    setUnavailable(null);
    setShowAsFile(true);
  };

  if (showAsFile) {
    return fileCard({ reason: null, showSourceMetadata: true });
  }

  if (unavailable) {
    return fileCard({ reason: unavailable });
  }

  if (!attachment.displaySource && !attachment.sourcePath) {
    return fileCard({ reason: fallbackUnavailable(kind) });
  }

  if (kind === 'image' && !attachment.displaySource && !hasNativeJSBridge()) {
    return attachment.sourcePath
      ? fileCard({ reason: null, showSourceMetadata: true })
      : fileCard({ reason: fallbackUnavailable(kind) });
  }

  if (kind === 'image') {
    return (
      <AttachmentImage
        src={attachment.url}
        fileName={attachment.fileName}
        mimeType={attachment.mimeType}
        sizeBytes={attachment.sizeBytes}
        sourcePath={attachment.sourcePath}
        environmentId={attachment.environmentId}
        sourceSessionId={attachment.nativePreviewSessionId}
        agentId={attachment.agentId}
        signature={attachment.signature}
        fileRefs={attachment.fileRefs}
        prompt={content.generationPrompt ?? undefined}
        compactActions={compactImageActions}
        allowDownload={allowImageDownload}
        cachedImageSize={cachedImageSize}
        className={imageClassName ?? className}
        data-artifact={artifactId}
        onImageLayout={onImageLayout}
        onUnavailable={() => handlePreviewUnavailable()}
      />
    );
  }

  if (kind === 'audio') {
    if (!mediaResolvedUrl) {
      return fileCard({
        reason: attachment.sourcePath ? null : fallbackUnavailable(kind),
        showSourceMetadata: Boolean(attachment.sourcePath),
      });
    }
    return (
      <CueStreamdownAudio
        src={mediaResolvedUrl}
        fileName={attachment.fileName}
        mimeType={attachment.mimeType}
        sizeBytes={attachment.sizeBytes}
        sourcePath={attachment.sourcePath}
        environmentId={attachment.environmentId}
        sourceSessionId={attachment.nativePreviewSessionId}
        artifactId={artifactId}
        className={className}
        onUnavailable={handlePreviewUnavailable}
      />
    );
  }

  if (kind === 'video') {
    if (!mediaResolvedUrl) {
      return fileCard({
        reason: attachment.sourcePath ? null : fallbackUnavailable(kind),
        showSourceMetadata: Boolean(attachment.sourcePath),
      });
    }
    return (
      <CueStreamdownVideo
        src={mediaResolvedUrl}
        fileName={attachment.fileName}
        mimeType={attachment.mimeType}
        sizeBytes={attachment.sizeBytes}
        sourcePath={attachment.sourcePath}
        environmentId={attachment.environmentId}
        sourceSessionId={attachment.nativePreviewSessionId}
        className={className}
        onUnavailable={handlePreviewUnavailable}
      />
    );
  }

  return fileCard();
};
